import React from 'react';
import { Box, Button, Modal, Typography } from '@mui/material';
import { brand, green, red } from '../../theme/themePrimitives';
/**
 * @heading1: CustomModal Component
 * @paragraph: This is a custom modal component that can be used throughout the application.
 */

/**
 * @interface: ModalInterface
 * @paragraph: Defines the structure of the props for the `CustomModal` component.
 * @property: {boolean} open - Determines whether the modal is visible.
 * @property: {function} handleClose - Callback function to close the modal.
 * @property: {React.ReactNode} children - The content to render inside the modal.
 * @property: {string} title - Optional title of the modal.
 * @property: {boolean} isError - Determines if the modal should display an error style.
 */
interface ModalInterface {
    open: boolean;
    handleClose: () => void;
    children: React.ReactNode;
    title?: string;
    isError?: boolean;
    isSuccess?: boolean;
    isWhite?: boolean;
    isBig?: boolean;
    isSmall?: boolean;
}

/**
 * @heading1: CustomModal
 * @paragraph: A reusable modal component with customizable title, error styles, and content.
 * @param: {ModalInterface} props - The props object adhering to the `ModalInterface`.
 * @returns: {JSX.Element} The rendered `CustomModal` component.
*/
const CustomModal = ({
    open,
    handleClose,
    children,
    title = '',
    isError = false,
    isSuccess = false,
    isWhite = false,
    isBig = false,
    isSmall = false
}: ModalInterface) => {
    /**
     * @blockquote: The default style object for the modal container.
     */
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isSmall ? 300 : isBig ? 800 : 500,
        bgcolor: 'background.paper',
        border: 'none',
        borderRadius: '8px',
        boxShadow: 24
    };
    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            {/* Modal container with styling */}
            <Box
                sx={style}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                boxSizing="border-box"
            >
                {/* Header with title and close button */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    boxSizing="border-box"
                    sx={{
                        backgroundColor: isWhite ? 'background.paper' : isError ? red[300] : isSuccess ? green[350] : brand[300],
                        borderTopRightRadius: '8px',
                        borderTopLeftRadius: '8px'
                    }}
                    paddingTop="12px"
                    paddingBottom="12px"
                >
                    {/* Make Title to align it center */}
                    <Box></Box>
                    {/* Title */}
                    <Typography
                        id="modal-title"
                        variant="h6"
                        fontSize="18px"
                        fontWeight="700"
                        width="100%"
                        align="center"
                        color={isWhite ? 'black' : 'white'}
                    >
                        {title}
                    </Typography>
                    {/* Close button */}
                    <Button
                        onClick={handleClose}
                        sx={{
                            minWidth: 'unset',
                            paddingX: 1,
                            marginRight: 2,
                            color: isWhite ? 'black' : 'white'
                        }}
                    >
                        &#x2715;
                    </Button>
                </Box>
                {/* Modal content */}
                <Box
                    boxSizing="border-box"
                    width="100%"
                    sx={{
                        p: 3,
                        pt: 0
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Modal>
    );
};

export default CustomModal;
